/**
 * 인증 서비스
 *
 * 관리자 인증 관련 API 호출 및 토큰 관리
 */

import apiClient from './api';
import { LoginCredentials, AuthResponse, AdminUser } from '../types/auth';
import { STORAGE_KEYS } from '../utils/constants';

/**
 * 관리자 로그인
 *
 * @param credentials - 로그인 정보 (이메일, 비밀번호)
 * @returns 로그인 응답 (Access Token 및 관리자 정보)
 * @throws {Error} 로그인 실패 시 에러 발생
 */
export const login = async (credentials: LoginCredentials): Promise<AuthResponse> => {
  const response = await apiClient.post<{ success: boolean; data: AuthResponse }>('/admin/login', credentials);
  
  if (!response.data.success) {
    throw new Error('로그인에 실패했습니다.');
  }
  
  const { access_token, admin } = response.data.data;
  
  // 토큰 저장 (sessionStorage 사용 - 탭 닫으면 자동 삭제, 이중 로그인 제한)
  saveToken(access_token);
  // 관리자 정보 저장
  saveAdminInfo(admin);
  
  return response.data.data;
};

/**
 * 현재 관리자 정보 조회
 *
 * @returns 현재 로그인한 관리자 정보
 * @throws {Error} 인증 실패 시 에러 발생
 */
export const getCurrentAdmin = async (): Promise<{ success: boolean; data: { admin: AdminUser } }> => {
  const response = await apiClient.get<{ success: boolean; data: { admin: AdminUser } }>('/admin/me');
  return response.data;
};

/**
 * 관리자 로그아웃
 *
 * @remarks 로그아웃 API 호출 실패해도 클라이언트에서 토큰 삭제는 진행합니다.
 */
export const logout = async (): Promise<void> => {
  try {
    await apiClient.post('/admin/logout');
  } catch (error) {
    // 로그아웃 API 실패해도 클라이언트에서 토큰 삭제는 진행
    console.error('Logout API error:', error);
  } finally {
    // 클라이언트에서 토큰 삭제
    sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.USER);
  }
};

/**
 * Access Token 저장
 *
 * @param token - Access Token
 * @remarks 보안을 위해 sessionStorage 사용 (탭 닫으면 자동 삭제, 이중 로그인 제한)
 */
export const saveToken = (token: string): void => {
  sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
};

/**
 * Access Token 조회
 *
 * @returns Access Token 또는 null
 */
export const getToken = (): string | null => {
  return sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
};

/**
 * Access Token 삭제
 */
export const removeToken = (): void => {
  sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
};

/**
 * 관리자 정보 저장
 *
 * @param admin - 관리자 정보 객체
 * @remarks 보안을 위해 sessionStorage 사용 (탭 닫으면 자동 삭제)
 */
export const saveAdminInfo = (admin: AdminUser): void => {
  sessionStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(admin));
};

/**
 * 관리자 정보 조회
 *
 * @returns 관리자 정보 객체 또는 null
 */
export const getAdminInfo = (): AdminUser | null => {
  const adminInfo = sessionStorage.getItem(STORAGE_KEYS.USER);
  return adminInfo ? JSON.parse(adminInfo) : null;
};

/**
 * 관리자 정보 삭제
 */
export const removeAdminInfo = (): void => {
  sessionStorage.removeItem(STORAGE_KEYS.USER);
};
